import { CheckCircle, TrendingDown, TrendingUp } from '@mui/icons-material';
import { Box, Chip, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

interface LevelCompareBadgeProps {
    level?: number;
    wantedLevel?: number;
    maxLevel?: number;
    variant?: 'chip' | 'progress' | 'arrow' | 'circle';
    title?: string;
    size?: 'small' | 'medium';
}

export default function LevelCompareBadge({
    level,
    wantedLevel,
    maxLevel,
    variant = 'chip',
    title,
    size = 'medium',
}: LevelCompareBadgeProps) {
    const theme = useTheme();
    const levelDefinitions = useSelector((state: AppState) => state.levelDefinition);
    const dynamicMaxLevel = maxLevel || levelDefinitions.length;

    const getLevelColor = (lvl?: number) => {
        if (!lvl) return theme.palette.grey[400];
        return theme.palette.levels?.[lvl as 1 | 2 | 3 | 4 | 5] || theme.palette.primary.main;
    };

    const isComplete = level !== undefined && wantedLevel !== undefined && level >= wantedLevel;
    const hasGap = level !== undefined && wantedLevel !== undefined && level < wantedLevel;

    if (variant === 'chip') {
        const displayText =
            level !== undefined && wantedLevel !== undefined ? `${level}/${wantedLevel}` : level?.toString() || '0';

        const chipColor = isComplete ? 'success' : hasGap ? 'warning' : 'default';
        const icon = isComplete ? <CheckCircle /> : hasGap ? <TrendingUp /> : undefined;

        return (
            <Chip
                icon={icon}
                label={displayText}
                title={title}
                size={size === 'small' ? 'small' : 'medium'}
                color={chipColor}
                variant="outlined"
                sx={{
                    height: size === 'small' ? '20px' : '24px',
                    fontSize: size === 'small' ? '0.75rem' : '0.875rem',
                }}
            />
        );
    }

    if (variant === 'progress') {
        return (
            <Box sx={{ display: 'flex', gap: 0.5 }} title={title}>
                {Array.from({ length: dynamicMaxLevel }, (_, i) => {
                    const levelNum = i + 1;
                    const isCurrent = levelNum <= (level || 0);
                    const isTarget = levelNum <= (wantedLevel || dynamicMaxLevel);

                    let segmentColor;
                    if (isCurrent) {
                        // Aktuelles Level - normale Level-Farbe
                        segmentColor = getLevelColor(levelNum);
                    } else if (isTarget) {
                        // Gewünschtes Level noch nicht erreicht - Warning-Farbe
                        segmentColor = theme.palette.warning.main;
                    } else {
                        // Nicht relevant - Grau
                        segmentColor = theme.palette.grey[300];
                    }

                    return (
                        <Box
                            key={i}
                            sx={{
                                width: size === 'small' ? 6 : 8,
                                height: size === 'small' ? 12 : 16,
                                borderRadius: 1,
                                backgroundColor: segmentColor,
                                transition: 'all 0.2s ease',
                            }}
                        />
                    );
                })}
            </Box>
        );
    }

    if (variant === 'arrow') {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} title={title}>
                <Chip
                    label={level || 0}
                    size={size === 'small' ? 'small' : 'medium'}
                    sx={{
                        bgcolor:
                            theme.palette.mode === 'dark' ? alpha(getLevelColor(level), 0.2) : getLevelColor(level),
                        color:
                            theme.palette.mode === 'dark'
                                ? getLevelColor(level)
                                : theme.palette.getContrastText(getLevelColor(level)),
                        border: theme.palette.mode === 'light' ? `1px solid ${getLevelColor(level)}` : 'none',
                        height: size === 'small' ? '20px' : '24px',
                        fontWeight: 600,
                    }}
                />
                {wantedLevel && (
                    <>
                        {isComplete ? (
                            <CheckCircle color="success" fontSize="small" />
                        ) : (
                            <TrendingUp color={hasGap ? 'warning' : 'action'} fontSize="small" />
                        )}
                        <Chip
                            label={wantedLevel}
                            size={size === 'small' ? 'small' : 'medium'}
                            sx={{
                                borderColor: getLevelColor(wantedLevel),
                                borderWidth: '2px',
                                borderStyle: 'dotted',
                                color:
                                    theme.palette.mode === 'dark'
                                        ? getLevelColor(wantedLevel)
                                        : theme.palette.getContrastText(getLevelColor(wantedLevel)),
                                bgcolor:
                                    theme.palette.mode === 'dark'
                                        ? alpha(getLevelColor(wantedLevel), 0.2)
                                        : getLevelColor(wantedLevel),
                                height: size === 'small' ? '20px' : '24px',
                                fontWeight: 600,
                            }}
                        />
                    </>
                )}
            </Box>
        );
    }

    if (variant === 'circle') {
        const currentLevel = level || 0;
        const targetLevel = wantedLevel || dynamicMaxLevel;

        return (
            <Box sx={{ display: 'flex', gap: 0.5 }} title={title}>
                {Array.from({ length: dynamicMaxLevel }, (_, i) => {
                    const levelNum = i + 1;
                    const isCurrent = levelNum <= currentLevel;
                    const isTarget = levelNum <= targetLevel;

                    return (
                        <Box
                            key={i}
                            sx={{
                                width: size === 'small' ? 8 : 12,
                                height: size === 'small' ? 8 : 12,
                                borderRadius: '50%',
                                backgroundColor: isCurrent
                                    ? getLevelColor(levelNum)
                                    : isTarget
                                      ? alpha(getLevelColor(levelNum), 0.3)
                                      : theme.palette.grey[300],
                                border: isTarget && !isCurrent ? `2px solid ${getLevelColor(levelNum)}` : 'none',
                                transition: 'all 0.2s ease',
                            }}
                        />
                    );
                })}
            </Box>
        );
    }

    return null;
}
